<div class="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

    <!-- Column Header — mirrors slot row structure for perfect alignment -->
    <div class="border-b border-gray-100 bg-gray-50/80 flex items-stretch flex-shrink-0">

        <!-- Time-label spacer (matches w-14 = 56px in slot rows) -->
        <div class="w-14 flex-shrink-0 border-r border-gray-200"></div>

        <!-- MemeTime sub-header -->
        <div class="flex-1 flex items-center gap-2 px-3 py-3 min-w-0">
            <svg class="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            <h2 class="font-semibold text-gray-700 text-sm">Activity</h2>
            <span class="text-xs text-gray-400 ml-auto">Apps &amp; Files</span>
        </div>

        <!-- Calendar sub-header (only when active or loading) -->
        @if (hasCalendar || calendarLoading) {
        <div class="flex-1 flex items-center gap-1.5 px-3 py-3 border-l border-blue-100 bg-blue-50/50 min-w-0">
            <svg class="w-3.5 h-3.5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V9h14v10z"/>
            </svg>
            <span class="text-xs font-semibold text-blue-700">Calendar</span>
            @if (calendarLoading) {
                <div class="ml-auto w-3.5 h-3.5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin flex-shrink-0"></div>
            }
        </div>
        }
    </div>

    <!-- Scrollable Timeline -->
    <div #scrollContainer
         class="flex-1 overflow-y-auto relative custom-scrollbar"
         (mouseleave)="hoverSlot.emit(null)">
        <div class="flex flex-col min-h-full relative">

            <!-- Current Time Indicator (NOW line) -->
            @if(currentLineTop > 0) {
                <div class="absolute w-full z-50 pointer-events-none flex items-center"
                     [style.top.px]="currentLineTop - 1">
                    <div class="w-3 h-3 bg-red-500 rounded-full -ml-1.5 shadow-sm ring-2 ring-red-200"></div>
                    <div class="flex-1 h-0.5 bg-gradient-to-r from-red-500 to-red-300"></div>
                    <span class="text-[10px] font-bold text-red-500 bg-white px-1.5 py-0.5 rounded-full shadow-sm mr-2">NOW</span>
                </div>
            }

            <!-- Time Slots -->
            @for (slot of slots; track slot.index) {
                <div class="flex border-b relative group box-border transition-colors duration-100"
                     [style.height.px]="rowHeight"
                     (mouseenter)="hoverSlot.emit(slot.index)"
                     [class.bg-blue-50]="hoveredSlotIndex === slot.index"
                     [class.bg-gray-50]="slot.isHourMark && hoveredSlotIndex !== slot.index"
                     [class.border-gray-200]="slot.isHourMark"
                     [class.border-gray-100]="!slot.isHourMark">

                    <!-- Time Label Column -->
                    <div class="w-14 text-xs text-right pr-3 select-none flex-shrink-0 border-r flex items-center justify-end"
                         [class.border-gray-200]="slot.isHourMark"
                         [class.border-gray-100]="!slot.isHourMark"
                         [class.font-semibold]="slot.isHourMark"
                         [class.text-gray-700]="slot.isHourMark"
                         [class.text-gray-400]="!slot.isHourMark"
                         [class.bg-gray-50]="slot.isHourMark">
                        {{ slot.label }}
                    </div>

                    <!-- MemeTime Activity Area -->
                    <div class="relative px-2 flex-1">
                        @let display = getSlotDisplay(slot.index);
                        @if(display && display.hasActivity && display.mainTask) {
                            <!-- Activity Block -->
                            <div class="absolute left-2 right-2 top-0.5 bottom-0.5
                                        bg-white border border-gray-200
                                        rounded-lg shadow-sm
                                        hover:shadow-md hover:border-blue-300 hover:bg-blue-50/30
                                        transition-all duration-150 cursor-pointer z-10 overflow-hidden
                                        group/item"
                                 (mouseenter)="hoverSlot.emit(slot.index)"
                                 (click)="onTaskClick($event, display)">

                                <!-- Left Accent Bar -->
                                <div class="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-l-lg"></div>

                                <!-- Content -->
                                <div class="pl-3 pr-2 h-full flex items-center gap-2">
                                    <div class="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                        <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                        </svg>
                                    </div>
                                    <span class="font-medium text-xs text-gray-700 truncate flex-1 group-hover/item:text-blue-700 transition-colors">
                                        {{ display.mainTask.title }}
                                    </span>
                                    @if(display.hiddenTasks.length > 0) {
                                        <span class="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0">
                                            +{{ display.hiddenTasks.length }}
                                        </span>
                                    }
                                    <svg class="w-3 h-3 text-gray-400 opacity-0 group-hover/item:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                                    </svg>
                                </div>
                            </div>
                        } @else {
                            <div class="h-full flex items-center">
                                <div class="w-full h-px bg-gray-100 group-hover:bg-blue-200 transition-colors"></div>
                            </div>
                        }
                    </div>

                    <!-- Calendar Gutter — inline per-slot rendering, always pixel-aligned -->
                    @if (hasCalendar || calendarLoading) {
                    @let calEvs = getCalendarEventsForSlot(slot.index);
                    <div class="flex-1 flex-shrink-0 border-l border-gray-200 relative overflow-hidden">

                        <!-- Skeleton shimmer while loading -->
                        @if (calendarLoading) {
                            @if (slot.index % 4 === 0) {
                                <div class="absolute inset-y-0.5 left-2 right-4 rounded animate-pulse bg-gray-100"></div>
                            } @else if (slot.index % 4 === 1) {
                                <div class="absolute inset-y-0.5 left-2 right-10 rounded animate-pulse bg-gray-50"></div>
                            }
                        } @else {
                            @for (ev of calEvs; track ev.id; let evIdx = $index) {
                                @let layout = getEventLayout(slot.index, ev.id);
                                @if (isFirstSlotOfEvent(slot.index, ev.id)) {
                                    <div class="absolute top-0 bottom-0 overflow-hidden flex items-start pt-0.5 px-1 gap-0.5"
                                         [style.left]="layout.left"
                                         [style.width]="layout.width"
                                         [style.backgroundColor]="getEventBg(ev)"
                                         [style.borderLeft]="'2px solid ' + getEventColor(ev)">
                                        @if (calEvs.length <= 3) {
                                            <svg class="w-2 h-2 flex-shrink-0 mt-px"
                                                 [style.color]="getEventColor(ev)"
                                                 viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3.9 3 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V9h14v10z"/>
                                            </svg>
                                            <span class="text-[9px] font-semibold truncate leading-tight"
                                                  [style.color]="getEventColor(ev)">{{ ev.summary }}</span>
                                        }
                                    </div>
                                } @else {
                                    <div class="absolute top-0 bottom-0"
                                         [style.left]="layout.left"
                                         [style.width]="layout.width"
                                         [style.backgroundColor]="getEventBg(ev)"
                                         [style.borderLeft]="'2px solid ' + getEventColor(ev) + 'a0'"></div>
                                }
                            }
                        }
                    </div>
                    }
                </div>
            }

            <!-- Empty State -->
            @if(slots.length === 0) {
                <div class="flex flex-col items-center justify-center py-16 text-gray-400">
                    <svg class="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    <span class="text-sm">No activity recorded</span>
                </div>
            }
        </div>
    </div>
</div>

@if(showMiniModal) {
    <app-mini-modal-detail-task
        [tasks]="selectedTasks"
        [position]="modalPosition"
        (close)="showMiniModal = false">
    </app-mini-modal-detail-task>
}
